/**
 * SigningCeremony — native PDF signing component for landlord signing flow
 * Fetches PDF + field placements from the public ceremony API, renders PDF with react-pdf,
 * overlays interactive field components, and submits on completion.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { getCeremonyDocument, submitCeremonyFields } from '../../lib/api';
import type { FieldPlacement, FieldValue } from './types';
import { SignatureField } from './SignatureField';
import { DateField } from './DateField';
import { TextField } from './TextField';
import { CheckboxField } from './CheckboxField';

// Worker is copied from pdfjs-dist@4.8.69 (must match react-pdf's bundled version) into public/
// so it's served from 'self' at /landlord/pdf.worker.min.mjs — CSP-safe, no CDN needed.
pdfjs.GlobalWorkerOptions.workerSrc = '/landlord/pdf.worker.min.mjs';

interface SigningCeremonyProps {
  /** Signing token — used with public ceremony API endpoints (no session required) */
  signingToken: string;
  onComplete?: () => void;
  onError?: (error: string) => void;
}

interface DocumentForSigningResponse {
  pdfBase64: string;
  fieldPlacements: Record<string, FieldPlacement[]>;
  recipientId: string;
  envelopeTitle: string;
}

/**
 * Extract and sort only THIS recipient's fields from the placement map.
 */
function getRecipientFields(
  fieldPlacements: Record<string, FieldPlacement[]>,
  recipientId: string
): FieldPlacement[] {
  const mine = fieldPlacements[recipientId] ?? [];
  return [...mine].sort((a, b) => {
    if (a.page !== b.page) return a.page - b.page;
    if (a.y !== b.y) return a.y - b.y;
    return a.x - b.x;
  });
}

function buildFieldValues(
  fields: FieldPlacement[],
  fieldValues: Record<number, string>
): FieldValue[] {
  return fields.map((f, i) => ({
    type: f.type,
    page: f.page,
    x: f.x,
    y: f.y,
    width: f.width,
    height: f.height,
    value: fieldValues[i] ?? (f.type === 'CHECKBOX' ? 'false' : ''),
  }));
}

function fieldLabel(field: FieldPlacement, index: number): string {
  const type = field.type === 'INITIALS' ? 'Initials' : field.type.charAt(0) + field.type.slice(1).toLowerCase();
  return `${type} (p${field.page}) #${index + 1}`;
}

function fieldTestIdSelector(field: FieldPlacement, globalIndex: number): string {
  const fieldType = field.type.toLowerCase();
  if (fieldType === 'signature' || fieldType === 'initials') return `signature-field-${globalIndex}`;
  if (fieldType === 'date') return `date-field-${globalIndex}`;
  if (fieldType === 'text') return `text-field-${globalIndex}`;
  return `checkbox-field-${globalIndex}`;
}

export function SigningCeremony({ signingToken, onComplete, onError }: SigningCeremonyProps): JSX.Element {
  const [pdfBase64, setPdfBase64] = useState<string | null>(null);
  const [fields, setFields] = useState<FieldPlacement[]>([]);
  const [fieldValues, setFieldValues] = useState<Record<number, string>>({});
  const [numPages, setNumPages] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(700);
  const [adoptedSignature, setAdoptedSignature] = useState<string | null>(null);
  const [visitedCheckboxIndices, setVisitedCheckboxIndices] = useState<Set<number>>(new Set());

  // The ref'd container below is NOT in the DOM while `loading` is true (this component
  // early-returns a spinner), so an observer attached on `[]` deps observed nothing and
  // `containerWidth` stayed at its 700px default: the PDF canvas stayed 700px wide regardless of
  // the viewport (horizontal scroll inside a narrower box, empty space in a wider one). Re-run
  // once `loading` flips to false so the real container is observed.
  useEffect(() => {
    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width;
      if (width && width > 0) setContainerWidth(width);
    });
    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [loading]);

  useEffect(() => {
    async function load() {
      try {
        setLoading(true);
        setError(null);
        const res = await getCeremonyDocument(signingToken) as DocumentForSigningResponse;
        setPdfBase64(res.pdfBase64);
        const sorted = getRecipientFields(res.fieldPlacements, res.recipientId);
        setFields(sorted);
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to load document';
        setError(msg);
        onError?.(msg);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [signingToken, onError]);

  function setFieldValue(index: number, value: string) {
    setFieldValues((prev) => ({ ...prev, [index]: value }));
    if (fields[index]?.type === 'CHECKBOX') {
      setVisitedCheckboxIndices((prev) => { const next = new Set(prev); next.add(index); return next; });
    }
  }

  function handleSignatureCapture(index: number, dataUrl: string) {
    setAdoptedSignature(dataUrl);
    setFieldValues((prev) => ({ ...prev, [index]: dataUrl }));
  }

  // Checkboxes are always considered filled (unchecked = valid "false" answer).
  // Only non-checkbox fields must have a truthy value to be considered filled.
  const totalRequired = fields.filter(f => f.type !== 'CHECKBOX').length;
  const filledCount = fields.reduce((count, f, i) => {
    if (f.type === 'CHECKBOX') return count; // skip checkboxes
    const val = fieldValues[i];
    return (val && val !== '' && val !== 'false') ? count + 1 : count;
  }, 0);
  const allFilled = fields.length > 0 && filledCount >= totalRequired;

  const scrollToField = useCallback((index: number) => {
    const field = fields[index];
    if (!field) return;
    if (field.type === 'CHECKBOX') {
      setVisitedCheckboxIndices((prev) => { const next = new Set(prev); next.add(index); return next; });
    }
    const selector = `[data-testid="${fieldTestIdSelector(field, index)}"]`;
    const el = document.querySelector(selector);
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [fields]);

  const nextUnfilledIndex = fields.findIndex((f, i) => {
    if (f.type === 'CHECKBOX') return !visitedCheckboxIndices.has(i); // Navigate to unvisited checkboxes
    const val = fieldValues[i];
    return !val || val === '' || val === 'false';
  });

  const prevFilledIndex = (() => {
    const cursor = nextUnfilledIndex === -1 ? fields.length : nextUnfilledIndex;
    for (let i = cursor - 1; i >= 0; i--) {
      return i;
    }
    return -1;
  })();

  async function handleSubmit() {
    try {
      setSubmitting(true);
      setSubmitError(null);
      const fieldValueList = buildFieldValues(fields, fieldValues);
      await submitCeremonyFields(signingToken, { fields: fieldValueList });
      onComplete?.();
    } catch (err) {
      // Distinct from the document-load `error` state above: a submit failure must NOT replace
      // the whole ceremony (that would discard every field the signer already filled in). Show an
      // inline, retryable error next to the submit controls instead. Intentionally not forwarded
      // via onError — the page-level action banner is for page-level actions, and forwarding here
      // would duplicate this same message in both places.
      const msg = err instanceof Error ? err.message : 'Failed to submit signature. Please try again.';
      setSubmitError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div
        data-testid="signing-ceremony-loading"
        className="flex flex-col items-center justify-center py-16 bg-card rounded-lg border border-border"
      >
        <div className="w-8 h-8 border-4 border-muted border-t-primary rounded-full animate-spin mb-4" />
        <p className="text-sm text-muted-foreground">Loading document...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div
        data-testid="signing-ceremony-error"
        className="bg-card rounded-lg border border-destructive/20 p-8 text-center"
      >
        <p className="text-sm font-medium text-destructive mb-2">Unable to load signing document</p>
        <p className="text-xs text-muted-foreground">{error}</p>
      </div>
    );
  }

  const pdfDataUri = pdfBase64 ? `data:application/pdf;base64,${pdfBase64}` : null;

  return (
    <div data-testid="signing-ceremony" className="relative pb-40 md:pb-0">
      {/* Desktop: Floating field navigation panel (hidden on mobile) */}
      <div
        data-testid="signing-field-nav"
        className="hidden md:flex fixed right-4 top-1/2 -translate-y-1/2 z-40 flex-col gap-2 bg-card rounded-lg border border-border shadow-lg p-3 w-48"
      >
        <div className="text-xs font-medium text-muted-foreground mb-1">
          {filledCount} / {totalRequired} fields
        </div>
        <div className="h-1.5 bg-muted rounded-full overflow-hidden mb-2">
          <div
            className="h-full bg-primary rounded-full transition-all duration-300"
            style={{ width: totalRequired > 0 ? `${(filledCount / totalRequired) * 100}%` : '0%' }}
          />
        </div>

        <div className="flex flex-col gap-1 max-h-48 overflow-y-auto">
          {fields.map((field, i) => {
            const isFilled = (field.type === 'CHECKBOX' ? visitedCheckboxIndices.has(i) : false) || (fieldValues[i] && fieldValues[i] !== '' && fieldValues[i] !== 'false');
            return (
              <button
                key={i}
                onClick={() => scrollToField(i)}
                className={`text-left text-xs px-2 py-1.5 rounded transition-colors min-h-[32px] ${
                  isFilled
                    ? 'text-success bg-success/10 hover:bg-success/10'
                    : 'text-muted-foreground bg-accent/50 hover:bg-accent'
                }`}
              >
                {isFilled ? '\u2713 ' : '\u25CB '}{fieldLabel(field, i)}
              </button>
            );
          })}
        </div>

        <div className="flex gap-1.5 mt-2 border-t border-border pt-2">
          <button
            data-testid="signing-nav-prev"
            onClick={() => { if (prevFilledIndex >= 0) scrollToField(prevFilledIndex); }}
            disabled={prevFilledIndex < 0}
            aria-label="Previous field"
            className="flex-1 py-2 text-xs font-medium text-muted-foreground bg-accent/50 border border-border rounded hover:bg-accent disabled:opacity-30 disabled:cursor-not-allowed min-h-[36px]"
          >
            Prev
          </button>
          <button
            data-testid="signing-nav-next"
            onClick={() => { if (nextUnfilledIndex >= 0) scrollToField(nextUnfilledIndex); }}
            disabled={nextUnfilledIndex < 0}
            aria-label="Next unfilled field"
            className="flex-1 py-2 text-xs font-medium text-primary bg-primary/5 border border-primary/20 rounded hover:bg-primary/10 disabled:opacity-30 disabled:cursor-not-allowed min-h-[36px]"
          >
            Next
          </button>
        </div>

        {submitError && (
          <p
            data-testid="signing-ceremony-submit-error"
            role="alert"
            className="text-xs text-destructive bg-destructive/10 rounded px-2 py-1.5 mt-1"
          >
            {submitError}
          </p>
        )}

        <button
          data-testid="signing-ceremony-submit-btn"
          onClick={handleSubmit}
          disabled={!allFilled || submitting}
          aria-disabled={!allFilled || submitting}
          className="w-full py-2.5 text-sm font-medium text-primary-foreground bg-primary rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px] mt-1"
        >
          {submitting ? 'Submitting...' : submitError ? 'Retry Signing' : 'Complete Signing'}
        </button>
      </div>

      {/* Mobile: Sticky bottom navigation bar (hidden on desktop) */}
      <div
        data-testid="signing-field-nav-mobile"
        className="md:hidden fixed bottom-[var(--bottom-tab-height)] left-0 right-0 z-40 bg-card border-t border-border shadow-[0_-4px_12px_rgba(0,0,0,0.1)] px-4 py-3"
      >
        {/* Progress bar */}
        <div className="flex items-center gap-3 mb-3">
          <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
            <div
              className="h-full bg-primary rounded-full transition-all duration-300"
              style={{ width: totalRequired > 0 ? `${(filledCount / totalRequired) * 100}%` : '0%' }}
            />
          </div>
          <span className="text-xs font-medium text-muted-foreground shrink-0">
            {filledCount}/{totalRequired}
          </span>
        </div>

        {/* Field chips — horizontal scroll */}
        <div className="flex gap-1.5 overflow-x-auto pb-2 -mx-1 px-1 scrollbar-none">
          {fields.map((field, i) => {
            const isFilled = (field.type === 'CHECKBOX' ? visitedCheckboxIndices.has(i) : false) || (fieldValues[i] && fieldValues[i] !== '' && fieldValues[i] !== 'false');
            return (
              <button
                key={i}
                onClick={() => scrollToField(i)}
                className={`shrink-0 text-xs px-2.5 py-1.5 rounded-full transition-colors min-h-[32px] ${
                  isFilled
                    ? 'text-success bg-success/10 border border-success/20'
                    : 'text-muted-foreground bg-accent/50 border border-border'
                }`}
              >
                {isFilled ? '\u2713 ' : '\u25CB '}{fieldLabel(field, i)}
              </button>
            );
          })}
        </div>

        {submitError && (
          <p
            data-testid="signing-ceremony-submit-error-mobile"
            role="alert"
            className="text-xs text-destructive bg-destructive/10 rounded px-2 py-1.5 mt-2"
          >
            {submitError}
          </p>
        )}

        {/* Nav + Submit row */}
        <div className="flex gap-2 mt-2">
          <button
            onClick={() => { if (prevFilledIndex >= 0) scrollToField(prevFilledIndex); }}
            disabled={prevFilledIndex < 0}
            aria-label="Previous field"
            className="px-4 py-2.5 text-sm font-medium text-muted-foreground bg-accent/50 border border-border rounded-lg disabled:opacity-30 disabled:cursor-not-allowed min-h-[44px]"
          >
            Prev
          </button>
          <button
            onClick={() => { if (nextUnfilledIndex >= 0) scrollToField(nextUnfilledIndex); }}
            disabled={nextUnfilledIndex < 0}
            aria-label="Next unfilled field"
            className="px-4 py-2.5 text-sm font-medium text-primary bg-primary/5 border border-primary/20 rounded-lg disabled:opacity-30 disabled:cursor-not-allowed min-h-[44px]"
          >
            Next
          </button>
          <button
            data-testid="signing-ceremony-submit-btn-mobile"
            onClick={handleSubmit}
            disabled={!allFilled || submitting}
            aria-disabled={!allFilled || submitting}
            className="flex-1 py-2.5 text-sm font-medium text-primary-foreground bg-primary rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px]"
          >
            {submitting ? 'Submitting...' : submitError ? 'Retry Signing' : 'Complete Signing'}
          </button>
        </div>
      </div>

      {/* PDF + field overlays */}
      <div ref={containerRef} className="bg-card rounded-lg border border-border overflow-auto md:mr-52 ph-no-capture">
        {pdfDataUri && (
          <Document
            file={pdfDataUri}
            onLoadSuccess={({ numPages }) => setNumPages(numPages)}
            onLoadError={(err) => {
              console.error('PDF load error:', err);
              setError(`PDF load error: ${err?.message ?? String(err)}`);
            }}
            loading={
              <div className="p-8 text-center text-sm text-muted-foreground">Loading PDF...</div>
            }
            error={
              <div className="p-8 text-center text-sm text-destructive">Failed to render PDF. Check browser console for details.</div>
            }
          >
            {Array.from({ length: numPages }, (_, idx) => {
              const pageNum = idx + 1;
              const pageFields = fields
                .map((f, i) => ({ field: f, globalIndex: i }))
                .filter(({ field }) => field.page === pageNum);

              return (
                <div key={pageNum}>
                  {pageNum > 1 && (
                    <div className="flex items-center gap-3 px-4 py-2 bg-muted/60 border-y border-border">
                      <div className="flex-1 h-px bg-border" />
                      <span className="text-xs font-medium text-muted-foreground shrink-0">
                        Page {pageNum} of {numPages}
                      </span>
                      <div className="flex-1 h-px bg-border" />
                    </div>
                  )}
                  <div
                    data-testid={`signing-page-${pageNum}`}
                    style={{
                      position: 'relative',
                      display: 'inline-block',
                      width: '100%',
                      boxShadow: pageNum < numPages ? '0 4px 8px -2px rgba(0,0,0,0.12)' : undefined,
                    }}
                  >
                  <Page
                    pageNumber={pageNum}
                    width={containerWidth}
                    renderAnnotationLayer={true}
                    renderTextLayer={true}
                  />
                  {pageFields.map(({ field, globalIndex }) => (
                    <div
                      key={globalIndex}
                      style={{
                        position: 'absolute',
                        left: `${field.x}%`,
                        top: `${field.y}%`,
                        width: `${field.width}%`,
                        height: `${field.height}%`,
                        zIndex: 10,
                      }}
                    >
                      {(field.type === 'SIGNATURE' || field.type === 'INITIALS') && (
                        <SignatureField
                          placement={field}
                          value={fieldValues[globalIndex] ?? null}
                          adoptedSignature={adoptedSignature}
                          onCapture={(dataUrl) => handleSignatureCapture(globalIndex, dataUrl)}
                          fieldIndex={globalIndex}
                        />
                      )}
                      {field.type === 'DATE' && (
                        <DateField
                          placement={field}
                          value={fieldValues[globalIndex] ?? null}
                          onChange={(iso) => setFieldValue(globalIndex, iso)}
                          fieldIndex={globalIndex}
                        />
                      )}
                      {field.type === 'TEXT' && (
                        <TextField
                          placement={field}
                          value={fieldValues[globalIndex] ?? ''}
                          onChange={(val) => setFieldValue(globalIndex, val)}
                          fieldIndex={globalIndex}
                        />
                      )}
                      {field.type === 'CHECKBOX' && (
                        <CheckboxField
                          placement={field}
                          value={fieldValues[globalIndex] === 'true'}
                          onChange={(checked) => setFieldValue(globalIndex, checked ? 'true' : 'false')}
                          fieldIndex={globalIndex}
                        />
                      )}
                    </div>
                  ))}
                  </div>
                </div>
              );
            })}
          </Document>
        )}
      </div>
    </div>
  );
}

export default SigningCeremony;
